import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {flatMap, takeUntil} from 'rxjs/operators';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {combineLatest, of, Subject} from 'rxjs';
import {MsgBoxService} from '../../shared/msg-box.service';
import {I18NService} from '../../i18n/i18n.service';
import {LoginService} from '../../shared/login.service';
import {environment} from '../../../environments/environment';
import {EntityType} from 'smag-commons/cfg/hierarchy';
import {SmagApiService} from '../../api/smag-api.service';
import {EMAIL_PATTERN, MOBILE_PATTERN} from '../../shared/patterns';
import {UserProfile} from 'auth-commons';
import {Scope} from 'smag-commons/dto/api';

const MOBILE_WIDTH_TH = 959;


@Component({
    selector: 'app-customer-care',
    templateUrl: './customer-care.component.html',
    styleUrls: ['./customer-care.component.scss']
})
export class CustomerCareComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();

    form: FormGroup;
    communities: HierarchyEntity[] = null;
    requestTypes: { [key: string]: string } = {};
    mobile = false;
    sendingRequest = false;

    constructor(private fb: FormBuilder,
                private readonly hierarchyService: HierarchyService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly loginService: LoginService,
                private readonly apiService: SmagApiService) {
        this.form = fb.group({
            communityId: new FormControl(null, [Validators.required]),
            requestType: new FormControl(null, [Validators.required]),
            firstName: new FormControl(null, [Validators.required]),
            lastName: new FormControl(null, [Validators.required]),
            email: new FormControl(null, [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
            mobile: new FormControl(null, [Validators.required, Validators.pattern(MOBILE_PATTERN)]),
            description: new FormControl(null, [Validators.required])
        });
    }

    ngOnInit() {
        this.isMobile();
        this.requestTypes = environment.customerCareRequests as { [key: string]: string };

        this.hierarchyService.findAllEntities(entity => entity.type === EntityType.ENERGY_COMMUNITY)
            .pipe(takeUntil(this.destroyed))
            .subscribe(communities => this.communities = communities);

        this.loginService.userProfile.pipe(
            takeUntil(this.destroyed),
            flatMap(profile => combineLatest([of(profile),
                this.apiService.findRepresentativeByU3id(profile.u3id),
                this.apiService.getSubjectInfo()])))
            .subscribe(([profile, repData, subjectInfo]) => {
                if (isAdvancedUser(profile)) {
                    this.form.controls.firstName.setValue(subjectInfo.subject.personal_data.first_name);
                    this.form.controls.lastName.setValue(subjectInfo.subject.personal_data.last_name);
                    this.form.controls.mobile.setValue(subjectInfo.subject.personal_data.mobile_number ?
                        subjectInfo.subject.personal_data.mobile_number : null);
                } else if (isRepresentative(profile)) {
                    this.form.controls.firstName.setValue(repData.personal_data.first_name);
                    this.form.controls.lastName.setValue(repData.personal_data.last_name);
                    this.form.controls.mobile.setValue(repData.personal_data.mobile_number ? repData.personal_data.mobile_number : null);
                } else {
                    this.form.controls.firstName.setValue(profile.firstName);
                    this.form.controls.lastName.setValue(profile.lastName);
                    this.form.controls.mobile.setValue(profile.mobile ? profile.mobile : null);
                }
                this.form.controls.email.setValue(profile.email);
            });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onSubmit() {
        this.sendingRequest = true;
        const request = {
            requestType: this.i18n.translate(this.form.controls.requestType.value),
            firstName: this.form.controls.firstName.value,
            lastName: this.form.controls.lastName.value,
            email: this.form.controls.email.value,
            mobile: this.form.controls.mobile.value,
            description: this.form.controls.description.value
        };

        this.apiService.sendCustomerCareRequest((this.form.controls.communityId.value as HierarchyEntity).id, request)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Request sent correctly'));
                this.resetForm();
            })
            .catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while sending request'), err))
            .finally(() => this.sendingRequest = false);
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.isMobile();
    }

    sortRequests = (a, b) => {
        return a.value.length > b.value.length ? -1 : 1;
    }

    private isMobile() {
        this.mobile = window.innerWidth <= MOBILE_WIDTH_TH;
    }

    private resetForm() {
        this.form.controls.communityId.reset();
        this.form.controls.requestType.reset();
        this.form.controls.description.reset();
    }

}

export function isAdvancedUser(profile: UserProfile) {
    return profile && profile.scopes?.includes(Scope.ADVANCED);
}

export function isRepresentative(profile: UserProfile) {
    return profile && profile.scopes?.includes(Scope.REPRESENTATIVE);
}

